import { type ReactNode } from 'react'

import type {
  ProspectAuditDto,
  ProspectAuditInstagramDto,
  ProspectAuditOnlyFansDto,
  ProspectAuditTikTokDto,
} from '@api'
import html2canvas from 'html2canvas'
import { Doughnut } from 'react-chartjs-2'
import { createRoot } from 'react-dom/client'

import {
  INSTAGRAM_AUDIT_OPTIONS,
  PERFORMANCE_BENCHMARKS,
  STATUS_COLORS,
  STATUS_POINTS,
  TIKTOK_AUDIT_OPTIONS,
} from './constants'
import {
  type AuditOptionWithStatus,
  type FormattedAudit,
  type FormattedSocialAuditItem,
  SocialAuditStatus,
} from './types'

const getChartColor = (percentage: number) => {
  if (percentage >= PERFORMANCE_BENCHMARKS[SocialAuditStatus.Good]) {
    return STATUS_COLORS[SocialAuditStatus.Good]
  }
  if (percentage >= PERFORMANCE_BENCHMARKS[SocialAuditStatus.Warning]) {
    return STATUS_COLORS[SocialAuditStatus.Warning]
  }

  return STATUS_COLORS[SocialAuditStatus.Critical]
}

const generateCanvasBody = async (element: ReactNode) => {
  const container = document.createElement('div')

  container.style.position = 'absolute'
  container.style.left = '-9999px'
  document.body.appendChild(container)

  const root = createRoot(container)

  root.render(element)
  // Wait for the chart to render
  await new Promise((resolve) => setTimeout(resolve, 500))

  return container
}

const generateChart = (percentage: number) => {
  const color = getChartColor(percentage)

  const chart = (
    <Doughnut
      height={400}
      width={400}
      data={{
        datasets: [
          {
            data: [percentage, 100 - percentage],
            borderRadius: 8,
            backgroundColor: [color, '#F1F2F4'],
          },
        ],
      }}
      options={{
        animation: false,
        aspectRatio: 1.5,
        rotation: -90,
        circumference: 180,
        offset: 6,
        cutout: '75%',
        maintainAspectRatio: true,
        responsive: true,
      }}
    />
  )

  return generateCanvasBody(chart)
}

const generateImage = (url: string) => {
  const image = (
    <img
      src={url}
      alt=""
      width={100}
      height={100}
    />
  )

  return generateCanvasBody(image)
}

const getCanvasImageFromUrl = async (url?: string) => {
  try {
    if (!url) return ''
    const res = await fetch(url)
    const blob = await res.blob()
    const blobUrl = URL.createObjectURL(blob)

    if (!blobUrl) return ''
    const canvas = await html2canvas(await generateImage(blobUrl))

    return canvas.toDataURL('image/png')
  } catch (error) {
    return ''
  }
}

const mapOptions = (
  type: 'instagram' | 'tiktok',
  socialData: ProspectAuditTikTokDto | ProspectAuditInstagramDto,
): {
  values: AuditOptionWithStatus<typeof socialData>[]
  overall: Record<SocialAuditStatus, number>
  percentage: number
} => {
  const options =
    type === 'instagram' ? INSTAGRAM_AUDIT_OPTIONS : TIKTOK_AUDIT_OPTIONS
  const overall = {
    [SocialAuditStatus.Good]: 0,
    [SocialAuditStatus.Warning]: 0,
    [SocialAuditStatus.Critical]: 0,
  }
  let points = 0
  const values = options
    .map((option) => {
      const foundOption = option.conditions.find((condition) =>
        condition.check(socialData),
      )

      if (!foundOption) {
        return null
      }
      const { status } = foundOption
      const statusColor = STATUS_COLORS[status]

      points += STATUS_POINTS[status]
      overall[status] += 1

      return {
        label: option.label,
        description: option.description,
        status,
        statusLabel: foundOption.dynamicStatusLabel
          ? foundOption.dynamicStatusLabel(socialData)
          : foundOption.statusLabel || '',
        comment: foundOption.comment,
        statusColor,
      }
    })
    .filter((option) => option !== null)

  const maxPoints = values.length * 1

  return {
    values,
    overall,
    percentage: Math.round((points / maxPoints) * 100),
  }
}

const formatSocial = (
  type: 'instagram' | 'tiktok',
  socialData?: ProspectAuditInstagramDto[] | ProspectAuditTikTokDto[],
) => {
  return Promise.all(
    (socialData ?? []).map(async (item) => {
      if (!item.accountUrl) return item
      const { values, percentage, overall } = mapOptions(type, item)
      const avatarUrl = await getCanvasImageFromUrl(item.avatarUrl)
      const chartCanvas = await html2canvas(await generateChart(percentage))

      return {
        type,
        accountUrl: item.accountUrl,
        avatarUrl,
        options: values,
        percentage,
        overall,
        chartImage: chartCanvas.toDataURL('image/png'),
      }
    }),
  ) as Promise<
    FormattedSocialAuditItem<
      ProspectAuditInstagramDto | ProspectAuditTikTokDto
    >[]
  >
}

const formatOnlyfans = (
  ofData?: ProspectAuditOnlyFansDto[],
): Promise<ProspectAuditOnlyFansDto[]> => {
  return Promise.all(
    (ofData ?? []).map(async (item) => {
      const avatarUrl = await getCanvasImageFromUrl(item.avatarUrl)

      return {
        ...item,
        avatarUrl,
      }
    }),
  )
}

export const formatProspectAudit = async (
  data: ProspectAuditDto,
): Promise<FormattedAudit> => {
  const [onlyfans, tiktok, instagram] = await Promise.all([
    formatOnlyfans(data.auditOnlyfansList),
    formatSocial('tiktok', data.auditTikTokList),
    formatSocial('instagram', data.auditInstagramList),
  ])

  const res = {
    info: data.prospectDto,
    onlyfans,
    tiktok,
    instagram,
  }

  return res
}
